// frontend/src/features/bapb/sections/EqualUniformSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import EUAutoAdjustRules, { defaultRules, sanitizePctInt } from "./EUAutoAdjustRules";
import LeaseUpCalculator from "./LeaseUpCalculator";
import CompMapModal from "./CompMapModal";
import { refreshValues as cadRefreshValues, getSupportedCads as cadGetSupportedCads } from "../cadApi";
import RefreshValuesModal from "../components/RefreshValuesModal";

/**
 * Equal & Uniform — merged:
 *  - Subject seed from Cover
 *  - CSV/XLSX import + mapping (with header synonym detection)
 *  - Column picker
 *  - Whole-number % inputs for adj buckets
 *  - Auto rules (Size/Age/Quality) panel; subject never adjusted; lock fields when rule enabled
 *  - Summary: Median Adj $/unit + Indicated Value (now nets optional deductions)
 */

/* ----------------- tiny helpers ----------------- */
const num = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).trim();
  if (!s) return 0;
  const cleaned = s.replace(/[$,%\s,]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};
// whole-number % (e.g., "5" or "5%") -> decimal (0.05)
const pctWholeToDec = (v) => {
  const s = String(v ?? "").trim().replace(/%/g, "");
  if (!s) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n / 100 : 0;
};
const fmtMoney0 = (v) =>
  v === null || v === undefined || isNaN(v) ? "—" : `$${Math.round(v).toLocaleString()}`;
const fmtMoney2 = (v) =>
  v === null || v === undefined || isNaN(v)
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const median = (arr) => {
  const xs = arr.filter((x) => Number.isFinite(x)).slice().sort((a, b) => a - b);
  if (xs.length === 0) return 0;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
};
const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r-${Math.random().toString(36).slice(2, 8)}`);

/* ----------------- CSV + optional XLSX parsing ----------------- */
function splitCsvLine(line) {
  const out = [];
  let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === "," && !inQ) {
      out.push(cur); cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}
function parseCsv(text) {
  const lines = String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (!lines.length) return { headers: [], rows: [] };
  const headers = splitCsvLine(lines[0] || "");
  const rows = lines.slice(1).map((ln) => splitCsvLine(ln));
  return { headers, rows };
}
async function parseXlsx(file) {
  if (!window.XLSX) throw new Error("XLSX parser not found. Make sure window.XLSX is available.");
  const buf = await file.arrayBuffer();
  const wb = window.XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = window.XLSX.utils.sheet_to_json(ws, { header: 1 });
  const headers = (rows[0] || []).map((c) => String(c ?? ""));
  const out = rows.slice(1).map((r) => r.map((c) => (c == null ? "" : String(c))));
  return { headers, rows: out };
}

/* ----------------- auto-map headers ----------------- */
const FIELD_DEFS = [
  { key: "account",        label: "Account #",            synonyms: ["account", "parcel", "id", "account number", "account #"] },
  { key: "address",        label: "Address",              synonyms: ["address", "site address", "situs", "location", "street", "full address", "addr"] },
  { key: "property_type",  label: "Property Type",        synonyms: ["property type", "use", "type", "ptype", "prop type", "category", "group", "group code"] },
  { key: "class",          label: "Quality",              synonyms: ["quality", "class", "building class", "quality class", "grade", "grade class"] },
  { key: "size",           label: "Size / Units",         synonyms: ["size", "units", "unit count", "sf", "sqft", "area", "gla", "bldg sf", "building sf", "net rentable"] },
  { key: "land_sf",        label: "Land SF",              synonyms: ["land sf", "land sqft", "land sq ft", "land area", "site land sf", "parcel land sf", "land size", "site sf", "lot sf", "lot sqft", "lot size"] },
  { key: "year_built",     label: "Year Built",           synonyms: ["year built", "yr built", "year", "built", "effective year built", "eyb"] },
  { key: "nbhd",           label: "IMA / Neighborhood",   synonyms: ["ima","ima code","neighborhood","nbhd","nbhd code","location code","loc code","cad location code","market area","neighborhood code"] },
  { key: "market_value",   label: "Total Value",          synonyms: ["total value","appraised value","market value","value","total appraised","assessed value"] },
  { key: "notes",          label: "Notes",                synonyms: ["notes","remarks","comment","comments"] },
  { key: "is_subject",     label: "Subject?",             synonyms: ["subject","is subject","role"] },
];
const INDEX_BY_KEY = Object.fromEntries(FIELD_DEFS.map((f, i) => [f.key, i]));

function autoMap(headers) {
  const map = {};
  headers.forEach((raw, idx) => {
    const h = String(raw || "").toLowerCase().replace(/[^a-z0-9]/g, " ").replace(/\s+/g, " ").trim();
    let best = null, score = 0;
    for (const f of FIELD_DEFS) {
      for (const candidate of [f.label, ...(f.synonyms || [])]) {
        const c = String(candidate).toLowerCase();
        if (!c) continue;
        if (h === c) { best = f.key; score = 100; break; }
        if (h.includes(c)) { if (score < 50) { best = f.key; score = 50; } }
      }
      if (score === 100) break;
    }
    map[idx] = best || null;
  });
  return map;
}

/* ----------------- small UI bits ----------------- */
function Checkbox({ checked, onChange, disabled, title }) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4 rounded border-slate-300"
      checked={!!checked}
      onChange={(e) => onChange?.(e.target.checked)}
      disabled={disabled}
      title={title}
    />
  );
}
function NumberCell({ value, onChange, suffix, className, disabled, inputMode = "text" }) {
  return (
    <div className={`flex items-center ${className || ""}`}>
      <input
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        inputMode={inputMode}
      />
      {suffix && <span className="ml-1 text-xs text-slate-500">{suffix}</span>}
    </div>
  );
}

/* ----------------- refresh results dialog ----------------- */
function RefreshResultsDialog({ result, onClose, onUncheckFailed }) {
  if (!result) return null;

  const failed = Array.isArray(result.failed) ? result.failed : [];
  const hasFailed = failed.length > 0;
  const isComplete = result.variant === "complete";
  const isError = result.variant === "error";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="w-[520px] max-w-[95vw] max-h-[85vh] flex flex-col rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div
            className={`h-9 w-9 rounded-full flex items-center justify-center text-lg ${
              isError
                ? "bg-rose-100 text-rose-700"
                : hasFailed
                ? "bg-amber-100 text-amber-700"
                : "bg-emerald-100 text-emerald-700"
            }`}
            aria-hidden
          >
            {isError ? "!" : hasFailed ? "⚠" : "✓"}
          </div>
          <h3 className="text-lg font-semibold text-slate-800">{result.title}</h3>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {isComplete && (
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Updated
                </div>
                <div className="mt-1 text-2xl font-bold text-emerald-800">
                  {result.updated ?? 0}
                </div>
              </div>
              <div
                className={`rounded-lg border p-3 ${
                  hasFailed
                    ? "border-rose-200 bg-rose-50"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <div
                  className={`text-xs font-semibold uppercase tracking-wide ${
                    hasFailed ? "text-rose-700" : "text-slate-500"
                  }`}
                >
                  Errors
                </div>
                <div
                  className={`mt-1 text-2xl font-bold ${
                    hasFailed ? "text-rose-800" : "text-slate-700"
                  }`}
                >
                  {failed.length}
                </div>
              </div>
            </div>
          )}

          {hasFailed && (
            <div className="mb-4">
              <div className="text-sm font-semibold text-slate-700 mb-1">
                Failed Accounts
              </div>
              <div className="text-xs text-slate-500 mb-2">
                These accounts could not be refreshed. You can uncheck them
                from use below.
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-slate-600 border-b border-slate-200">
                          Account
                        </th>
                        <th className="text-left px-3 py-2 font-medium text-slate-600 border-b border-slate-200">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {failed.map((f, i) => (
                        <tr key={`${f.account}-${i}`} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono text-xs text-slate-800">
                            {f.account}
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-600">
                            {f.error}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {result.harrisNote && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="text-sm font-semibold text-amber-800 mb-1">
                Harris County Note
              </div>
              <p className="text-sm text-amber-700">{result.harrisNote}</p>
            </div>
          )}

          {result.message && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">
              {result.message}
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          {hasFailed && onUncheckFailed && (
            <button
              type="button"
              onClick={() => onUncheckFailed(failed.map((f) => f.account))}
              className="px-3 py-1.5 rounded-md border border-rose-300 bg-rose-50 text-rose-800 text-sm font-medium hover:bg-rose-100"
            >
              Uncheck {failed.length} Failed {failed.length === 1 ? "Row" : "Rows"}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 text-sm hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------- mapping dialog ----------------- */
function MappingDialog({ open, headers, sampleRows, initialFieldToIndex, onCancel, onImport, unitNoun }) {
  const FIELDS_TO_SHOW = ["account","address","property_type","class","size","land_sf","year_built","nbhd","market_value","notes","is_subject"];
  const [fieldToIndex, setFieldToIndex] = useState(initialFieldToIndex || {});
  const [subjectMode, setSubjectMode] = useState("auto"); // "auto" | "firstrow"

  useEffect(() => { setFieldToIndex(initialFieldToIndex || {}); }, [initialFieldToIndex, open]);
  if (!open) return null;

  const first3 = sampleRows.slice(0, 3);

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30">
      <div className="w-[720px] max-w-[95vw] rounded-xl border border-slate-200 bg-white shadow-lg">
        <div className="px-4 py-3 border-b">
          <div className="font-semibold">Map Columns</div>
          <div className="text-xs text-slate-500">We guessed based on your headers; tweak if needed, then Import.</div>
        </div>

        <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
          {FIELDS_TO_SHOW.map((key) => {
            const def = FIELD_DEFS[INDEX_BY_KEY[key]];
            const idx = fieldToIndex[key] ?? -1;
            return (
              <div key={key} className="grid grid-cols-5 gap-2 items-center">
                <div className="text-sm col-span-2">
                  <div className="font-medium">{def?.label}</div>
                  {key === "size" && (
                    <div className="text-xs text-slate-500">
                      Interpreted as {unitNoun === "SF" ? "SF" : unitNoun}
                    </div>
                  )}
                </div>
                <div className="col-span-3">
                  <select
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                    value={idx}
                    onChange={(e) => setFieldToIndex((p) => ({ ...p, [key]: Number(e.target.value) }))}
                  >
                    <option value={-1}>(not mapped)</option>
                    {headers.map((h, i) => (
                      <option key={i} value={i}>{h || `(column ${i + 1})`}</option>
                    ))}
                  </select>
                  {idx >= 0 && (
                    <div className="mt-1 text-[11px] text-slate-500 truncate">
                      {first3.map((r) => String(r[idx] ?? "")).filter(Boolean).slice(0,2).join(" · ") || "—"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div className="pt-2 border-t">
            <div className="text-sm font-medium mb-1">Subject row</div>
            <label className="mr-3 text-sm">
              <input type="radio" name="subjmode" checked={subjectMode === "auto"} onChange={() => setSubjectMode("auto")} className="mr-1" />
              Auto-detect (match account/address to Cover)
            </label>
            <label className="text-sm">
              <input type="radio" name="subjmode" checked={subjectMode === "firstrow"} onChange={() => setSubjectMode("firstrow")} className="mr-1" />
              First row is Subject
            </label>
          </div>
        </div>

        <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg px-3 py-1.5 border border-slate-300 bg-white">Cancel</button>
          <button onClick={() => onImport(fieldToIndex, subjectMode)} className="rounded-lg px-3 py-1.5 border border-sky-300 bg-sky-50 text-sky-800">
            Import
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------- Section ----------------- */
export default function EqualUniformSection({ packet, data, onChange, onSave }) {
  const initial = data?.equal_uniform || {};
  const [rows, setRows] = useState(Array.isArray(initial.rows) ? initial.rows : []);
  const [rules, setRules] = useState(initial.config?.auto_rules || defaultRules());

  // Default columns map
  const DEFAULT_COLUMNS = {
    use: true,
    account: true,
    address: true,
    property_type: true,
    class: false,       // key stays "class", label reads "Quality"
    size: true,
    land_sf: true,
    lb_ratio: true,
    year_built: true,
    nbhd: true,
    market_value: true,
    per_unit: true,               // $/unit (derived)
    adj_size_pct: true,
    adj_age_pct: true,
    adj_quality_pct: false,
    adj_land_ratio_pct: true,
    adj_location_pct: false,
    adj_custom_pct: false,
    adj_sum_pct: true,            // Sum of all adjustment %s (computed)
    adj_per_unit: true,           // derived
    adj_total_value: true,        // derived
    notes: false,
  };

  const [config, setConfig] = useState(() => ({
    subjectId: initial.config?.subjectId || null,
    columns: { ...DEFAULT_COLUMNS, ...(initial.config?.columns || {}) },
  }));
  const [showPicker, setShowPicker] = useState(false);

  // ---------- NEW: Deductions state ----------
  const [deductionType, setDeductionType] = useState(
    (initial.deduction_type || "none").toLowerCase()
  );
  const [manualDeduction, setManualDeduction] = useState(
    initial.manual_deduction ?? ""
  );
  const [leaseUp, setLeaseUp] = useState(initial.lease_up || null);
  // E&U advanced does not derive PGI/vacancy from rents—ask for it:
  const [luPGI, setLuPGI] = useState(initial.pgi_total ?? "");
  const [luVac, setLuVac] = useState((initial.vacancy ?? "").toString());
  const [luOpex, setLuOpex] = useState(initial.opex_total ?? "");

  // NEW: refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  // Comp selection criteria
  const [compCriteria, setCompCriteria] = useState(() => ({
    nbhd_area: "",
    radius: "",
    type: "",
    age_min: "",
    age_max: "",
    size_min: "",
    size_max: "",
    class: "",
    none_omitted: false,
    ...(initial.comp_criteria || {}),
  }));
  const updateCriteria = (patch) => setCompCriteria((p) => ({ ...p, ...patch }));

  // Comp map state
  const [compMapOpen, setCompMapOpen] = useState(false);
  const [compMapData, setCompMapData] = useState(initial.map_data || null);

  // NEW: dynamic supported CAD list for value scraping tooltip
  const [supportedCads, setSupportedCads] = useState([]);
  const [showCadInfo, setShowCadInfo] = useState(false);
  useEffect(() => {
    let alive = true;
    cadGetSupportedCads()
      .then((list) => {
        if (!alive) return;
        setSupportedCads(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        // Silent: tooltip can still render without list
      });
    return () => {
      alive = false;
    };
  }, []);

  // -------- Cover context (subject + unit label + taxYear) --------
  const coverCtx = useMemo(() => {
    let latestCover = null, maxOrder = -Infinity;
    for (const s of packet?.sections || []) {
      if (String(s?.type || "").toLowerCase() !== "cover") continue;
      const oi = s?.order_index ?? 0;
      if (oi > maxOrder) { maxOrder = oi; latestCover = s; }
    }
    const coverMeta = latestCover?.data || {};
    const pi = coverMeta?.property_info || {};
    const unitType = (pi.unit_type || "").toString().toUpperCase() || "UNITS";
    const sizeFromCover =
      Number(String(pi.units ?? pi.sqft ?? pi.area_sf ?? "").replace(/[$,%\s,]/g, "")) || 0;
    const landSfFromCover =
      Number(String(pi.land_sf ?? pi.land_area_sf ?? "").replace(/[$,%\s,]/g, "")) || 0;

    // ✅ Year comes ONLY from packet header
    const taxYear =
      Number(String(packet?.tax_year ?? "").replace(/\D/g, "")) ||
      new Date().getFullYear();

    return {
      account: pi.account || pi.parcel || "",
      address: pi.address || pi.site_name || "",
      property_type: pi.property_type || "",
      year_built: pi.year_built || "",
      nbhd: pi.cad_location_code || "",
      market_value: pi.current_year_value || pi.appraised_value || "",
      unitType,
      sizeFromCover,
      landSfFromCover,
      taxYear,
      coverMeta, // keep sending to backend for CAD auto-detect
    };
  }, [packet]);
  const unitNoun = coverCtx.unitType === "SF" ? "SF" : coverCtx.unitType === "BEDS" ? "Bed" : "Unit";
  const perUnitLabel = coverCtx.unitType === "SF" ? "$ / SF" : `$ / ${unitNoun}`;

  // Distinct qualities (for the reorder pills UI)
  const qualityValues = useMemo(() => {
    const set = new Set(
      (rows || [])
        .map((r) => (r?.class ?? "").toString().trim())
        .filter((s) => s !== "")
    );
    return Array.from(set);
  }, [rows]);

  // Seed Subject from Cover if grid empty
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!rows || rows.length === 0) {
      const subjectRow = {
        id: uid(),
        use: false, // subject is never a comp
        account: coverCtx.account || "",
        address: coverCtx.address || "",
        property_type: coverCtx.property_type || "",
        class: "",
        size: coverCtx.sizeFromCover ? String(coverCtx.sizeFromCover) : "",
        land_sf: coverCtx.landSfFromCover ? String(coverCtx.landSfFromCover) : "",
        year_built: coverCtx.year_built || "",
        nbhd: coverCtx.nbhd || "",
        market_value: coverCtx.market_value || "",
        adj_size_pct: "", adj_age_pct: "", adj_quality_pct: "", adj_land_ratio_pct: "", adj_location_pct: "", adj_custom_pct: "",
        notes: "",
      };
      setRows([subjectRow]);
      setConfig((p) => ({ ...p, subjectId: subjectRow.id }));
      seededRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(coverCtx), rows?.length]);

  // Derived lookups
  const subject = useMemo(() => rows.find((r) => r.id === config.subjectId) || null, [rows, config.subjectId]);
  const comps   = useMemo(() => rows.filter((r) => r.id !== config.subjectId), [rows, config.subjectId]);
  const usedComps = useMemo(() => comps.filter((r) => !!r.use), [comps]);

  // Keep saved map pins in sync with current used comps — remove unchecked, re-label sequentially
  useEffect(() => {
    if (!compMapData?.pins?.length) return;
    const usedIds = new Set(usedComps.map((r) => r.id));
    const subjectPin = compMapData.pins.find((p) => p.isSubject);
    const compPins = compMapData.pins.filter((p) => !p.isSubject && usedIds.has(p.id));
    // Re-label comp pins sequentially
    const relabeled = compPins.map((p, i) => ({ ...p, label: String(i + 1) }));
    const next = [...(subjectPin ? [subjectPin] : []), ...relabeled];
    // Only update if something actually changed (avoid infinite loop)
    const prevLabels = compMapData.pins.map((p) => `${p.id}:${p.label}`).join(",");
    const nextLabels = next.map((p) => `${p.id}:${p.label}`).join(",");
    if (prevLabels !== nextLabels) {
      setCompMapData((prev) => ({ ...prev, pins: next }));
    }
  }, [usedComps, compMapData]);

  // With calculations
  const withCalcs = useMemo(() => {
    return rows.map((r) => {
      const size = num(r.size);
      const mv = num(r.market_value);
      const land = num(r.land_sf);
      const ratio = size > 0 ? land / size : 0;
      const base = size > 0 ? mv / size : 0; // base $/unit
      const adjSum =
        pctWholeToDec(r.adj_size_pct) +
        pctWholeToDec(r.adj_age_pct) +
        pctWholeToDec(r.adj_quality_pct) +
        pctWholeToDec(r.adj_land_ratio_pct) +
        pctWholeToDec(r.adj_location_pct) +
        pctWholeToDec(r.adj_custom_pct);
      const adjPerUnit = base * (1 + adjSum);
      const adjTotal = adjPerUnit * (size || 0);
      return {
        ...r,
        _lb_ratio: ratio,
        _per_unit: base,
        _adj_per_unit: adjPerUnit,
        _adj_total_value: adjTotal,
        _adj_sum_pct: adjSum,
      };
    });
  }, [rows]);

  const stats = useMemo(() => {
    const byId = Object.fromEntries(withCalcs.map((r) => [r.id, r]));
    const adjPerUnits = usedComps.map((r) => byId[r.id]?._adj_per_unit || 0);
    const med = median(adjPerUnits);
    const indicated = subject ? num(subject.size) * med : 0;
    return { count: usedComps.length, median_adj_per_unit: med, indicated_value: indicated };
  }, [withCalcs, usedComps, subject]);

  // ---------- NEW: deductions for net indicated ----------
  const luVacNum = useMemo(() => {
    const s = String(luVac ?? "").replace(/[^0-9.-]/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  }, [luVac]);
  const deductionAmt = useMemo(() => {
    if (deductionType === "lease_up") return Number(leaseUp?.deduction || 0);
    if (deductionType === "manual") return num(manualDeduction);
    return 0;
  }, [deductionType, leaseUp, manualDeduction]);
  const indicatedNet = useMemo(
    () => Math.max(0, (stats.indicated_value || 0) - (deductionAmt || 0)),
    [stats.indicated_value, deductionAmt]
  );

  // Push up to PacketBuilder on any change (now includes net & deduction payload)
  const firstMount = useRef(true);
  useEffect(() => {
    if (firstMount.current) { firstMount.current = false; }
    onChange?.({
      equal_uniform: {
        rows,
        config: { ...config, auto_rules: rules, subjectId: config.subjectId },
        indicated_value: indicatedNet,                            // net of deductions
        indicated_value_before_deductions: stats.indicated_value, // gross
        deduction_type: deductionType,
        manual_deduction: num(manualDeduction || 0),
        lease_up: leaseUp || null,
        pgi_total: num(luPGI || 0),                               // for PDF Lease-Up
        vacancy: `${luVacNum}%`,                                  // for PDF Lease-Up
        opex_total: num(luOpex || 0),                             // for PDF Lease-Up
        map_data: compMapData || null,
        comp_criteria: { ...compCriteria, unit_noun: unitNoun },
        summary: {
          comps_used: stats.count,
          median_adj_per_unit: stats.median_adj_per_unit,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rows, config, rules,
    stats.indicated_value, stats.count, stats.median_adj_per_unit,
    indicatedNet, deductionType, manualDeduction, leaseUp, luPGI, luVacNum,
    compMapData, compCriteria
  ]);

  /* ------------- import (CSV + optional XLSX) ------------- */
  const [mapUI, setMapUI] = useState({ open: false, headers: [], sample: [], guessed: {} });
  const [pendingRows, setPendingRows] = useState([]);

  function toFieldToIndex(guessedHeaderIdxToField) {
    const fieldToIndex = {};
    for (const [colIdx, key] of Object.entries(guessedHeaderIdxToField || {})) {
      if (key) fieldToIndex[key] = Number(colIdx);
    }
    return fieldToIndex;
  }
  function mapRowFromFieldToIndex(cells, f2i) {
    const get = (key) => {
      const idx = f2i[key];
      return idx == null || idx < 0 ? "" : (cells[idx] ?? "");
    };
    const row = {
      id: uid(),
      use: true,
      account: get("account"),
      address: get("address"),
      property_type: get("property_type"),
      class: get("class"),
      size: get("size"),
      land_sf: get("land_sf"),
      year_built: get("year_built"),
      nbhd: get("nbhd"),
      market_value: get("market_value"),
      adj_size_pct: "", adj_age_pct: "", adj_quality_pct: "", adj_land_ratio_pct: "", adj_location_pct: "", adj_custom_pct: "",
      notes: get("notes"),
    };
    // hint subject match
    row._matchCover =
      (!!coverCtx.account && String(row.account).trim() === String(coverCtx.account).trim()) ||
      (!!coverCtx.address && String(row.address).toLowerCase().includes(String(coverCtx.address).toLowerCase()));
    row._explicitSubject = /^y(es)?|true|subject$/i.test(String(get("is_subject")));
    return row;
  }
  async function openFileAndPrepareMapping(file) {
    let parsed = { headers: [], rows: [] };
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (ext === "xlsx") {
      try { parsed = await parseXlsx(file); }
      catch (e) { alert(e?.message || "Failed to parse Excel. Ensure window.XLSX is available."); return; }
    } else {
      const text = await file.text();
      parsed = parseCsv(text);
    }
    const guessed = autoMap(parsed.headers);
    setMapUI({ open: true, headers: parsed.headers, sample: parsed.rows.slice(0, 10), guessed });
    setPendingRows(parsed.rows);
  }
  function confirmImport(fieldToIndex, subjectMode) {
    const newRows = (pendingRows || []).map((cells) => mapRowFromFieldToIndex(cells, fieldToIndex));
    if (!newRows.length) { setMapUI({ open: false, headers: [], sample: [], guessed: {} }); return; }

    // choose subject
    let subj = null;
    if (subjectMode === "firstrow") {
      subj = newRows[0];
    } else {
      subj = newRows.find((r) => r._explicitSubject || r._matchCover) || newRows[0];
    }
    delete subj._explicitSubject; delete subj._matchCover;

    // set rows, mark subject not used as comp
    const imported = [subj, ...newRows.filter((r) => r !== subj).map((r) => ({ ...r, use: true }))];
    setRows(imported.map((r) => (r === subj ? { ...r, use: false } : r)));
    setConfig((prev) => ({ ...prev, subjectId: subj.id }));
    setMapUI({ open: false, headers: [], sample: [], guessed: {} });
    setPendingRows([]);

    // optional: apply current auto rules immediately
    applyAutoAdjustments(imported, subj);
  }
  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    openFileAndPrepareMapping(f);
    e.target.value = "";
  }

  /* ------------- auto rules (apply) ------------- */
  function applyAutoAdjustments(sourceRows, subjRow) {
    // Determine subject ID and row in a way that does not depend on possibly-stale config/rows defaults
    const subjId = subjRow?.id ?? config.subjectId;
    if (!subjId) return;

    const subj =
      subjRow ||
      rows.find((r) => r.id === subjId) ||
      null;
    if (!subj) return;

    const subjSize = num(subj.size);
    const subjYear = num(subj.year_built);
    const subjQuality = (subj.class || "").toString();

    const qOrder = (rules.quality?.order || []).map((q) =>
      q == null ? "" : String(q)
    );
    const qIndex = (q) => {
      const s = (q == null ? "" : String(q)).trim().toLowerCase();
      let idx = qOrder.findIndex(
        (x) => String(x || "").trim().toLowerCase() === s
      );
      if (idx === -1) idx = qOrder.length; // unseen qualities are treated as lowest (at end)
      return idx;
    };

    setRows((prev) => {
      // If caller passed a specific snapshot (e.g., import), use that; otherwise use latest rows
      const base = sourceRows && sourceRows.length ? sourceRows : prev;

      return base.map((r) => {
        const isSubject = r.id === subjId;
        if (isSubject) return r; // never adjust subject

        let sizeAdj = "";
        let ageAdj = "";
        let qualAdj = "";
        let landRatioAdj = "";

        // SIZE
        if (
          rules.size?.enabled &&
          subjSize > 0 &&
          num(r.size) > 0 &&
          num(rules.size.amountPer) > 0
        ) {
          const diff = num(r.size) - subjSize; // + => comp larger
          const steps = Math.abs(diff) / num(rules.size.amountPer);
          const basePct = num(rules.size.pctPer) * steps;
          const dir =
            diff >= 0
              ? rules.size.largerDirection || "up"
              : rules.size.smallerDirection || "down";
          sizeAdj = dir === "up" ? String(round2(basePct)) : String(-round2(basePct));
        }

        // AGE
        if (
          rules.age?.enabled &&
          subjYear > 0 &&
          num(r.year_built) > 0 &&
          num(rules.age.yearsPer) > 0
        ) {
          const diff = num(r.year_built) - subjYear; // + => comp newer
          const steps = Math.abs(diff) / num(rules.age.yearsPer);
          const basePct = num(rules.age.pctPer) * steps;
          const dir =
            diff < 0
              ? rules.age.olderDirection || "up"
              : rules.age.newerDirection || "down";
          ageAdj = dir === "up" ? String(round2(basePct)) : String(-round2(basePct));
        }

        // QUALITY
        if (rules.quality?.enabled && Array.isArray(qOrder) && qOrder.length) {
          const sIdx = qIndex(subjQuality);
          const cIdx = qIndex(r.class);
          const steps = Math.abs(cIdx - sIdx);
          if (steps === 0) {
            qualAdj = "0";
          } else {
            const basePct = num(rules.quality.pctPerStep) * steps;
            const dir =
              cIdx < sIdx
                ? rules.quality.superiorDirection || "down"
                : rules.quality.inferiorDirection || "up";
            qualAdj = dir === "up" ? String(round2(basePct)) : String(-round2(basePct));
          }
        }

        // LAND / BUILDING RATIO
        if (
          rules.landRatio?.enabled &&
          subjSize > 0 &&
          num(subj.land_sf) > 0 &&
          num(r.size) > 0 &&
          num(r.land_sf) > 0 &&
          num(rules.landRatio.ratioPer) > 0
        ) {
          const subjRatio = num(subj.land_sf) / subjSize;
          const compRatio = num(r.land_sf) / num(r.size);
          const diff = compRatio - subjRatio; // + => comp has higher land/building ratio
          const steps = Math.abs(diff) / num(rules.landRatio.ratioPer);
          const basePct = num(rules.landRatio.pctPer) * steps;

          if (steps === 0) {
            landRatioAdj = "0";
          } else {
            const dir =
              diff >= 0
                ? rules.landRatio.higherDirection || "down"
                : rules.landRatio.lowerDirection || "up";
            landRatioAdj = dir === "up" ? String(round2(basePct)) : String(-round2(basePct));
          }
        }

        return {
          ...r,
          adj_size_pct: sizeAdj,
          adj_age_pct: ageAdj,
          adj_quality_pct: qualAdj,
          adj_land_ratio_pct: landRatioAdj,
        };
      });
    });
  }

  function round2(x) { return Math.round(Number(x) * 100) / 100; }

  /* ------------- CAD Refresh helper (auto-detect CAD via cover_meta) ------------- */
  async function doRefreshFromCAD() {
    try {
      setIsRefreshing(true);

      const year = Number(coverCtx.taxYear || new Date().getFullYear());

      // All non-empty accounts (subject + comps), de-duped
      // Expand comma-separated accounts so multi-parcel subjects work
      const allAccounts = Array.from(
        new Set(
          rows
            .flatMap((r) => String(r.account || "").split(",").map((a) => a.trim()))
            .filter((a) => a.length > 0)
            .map((a) => a.toUpperCase())
        )
      );

      if (!allAccounts.length) {
        setRefreshResult({
          variant: "info",
          title: "No Accounts",
          message: "No account numbers found in the grid.",
          failed: [],
        });
        return;
      }

      // Let backend guess CAD from cover_meta; no cad_key here.
      const resp = await cadRefreshValues({
        year,
        accounts: allAccounts,
        cover_meta: coverCtx.coverMeta,
      });

      // Build per-account value map
      const perAcctMap = new Map(
        (resp?.results || [])
          .filter((r) => r.ok && r.market_value != null)
          .map((r) => [String(r.account).trim().toUpperCase(), Number(r.market_value)])
      );

      // For multi-parcel rows (comma-separated), sum values across all parcels
      let updated = 0;
      setRows((prev) =>
        prev.map((r) => {
          const raw = String(r.account || "").trim();
          if (!raw) return r;
          const parts = raw.split(",").map((a) => a.trim().toUpperCase()).filter(Boolean);
          const vals = parts.map((p) => perAcctMap.get(p)).filter((v) => v != null);
          if (vals.length) {
            updated++;
            const sum = vals.reduce((a, b) => a + b, 0);
            return { ...r, market_value: String(sum) };
          }
          return r;
        })
      );

      const failed = (resp?.results || [])
        .filter((r) => !r.ok)
        .map((r) => ({
          account: String(r.account || "").trim().toUpperCase(),
          error: r.error || r.reason || "No data returned",
        }));
      const isHarris = /harris|hcad/i.test(coverCtx?.coverMeta?.property_info?.assessor || "");
      const harrisNote =
        isHarris && updated > 0
          ? "Harris County values are from the initial appraisal roll, not live HCAD data."
          : null;
      setRefreshResult({
        variant: "complete",
        title: "CAD Refresh Complete",
        updated,
        failed,
        harrisNote,
      });
    } catch (err) {
      console.error(err);
      setRefreshResult({
        variant: "error",
        title: "CAD Refresh Failed",
        message: String(err?.message || err),
        failed: [],
      });
    } finally {
      setIsRefreshing(false);
    }
  }

  /* ------------- table editing ------------- */
  const C = config.columns;
  function setRow(id, patch) { setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r))); }
  function removeRow(id) { setRows((prev) => prev.filter((r) => r.id !== id)); if (config.subjectId === id) setConfig((p) => ({ ...p, subjectId: null })); }
  function addEmptyComp() {
    const row = {
      id: uid(), use: true, account: "", address: "", property_type: "", class: "",
      size: "", land_sf: "", year_built: "", nbhd: "", market_value: "",
      adj_size_pct: "", adj_age_pct: "", adj_quality_pct: "", adj_land_ratio_pct: "", adj_location_pct: "", adj_custom_pct: "",
      notes: "",
    };
    setRows((prev) => [...prev, row]);
  }

  // fixed visual order (Role is always first and sticky)
  const ORDER = [
    "use","account","address","property_type","class","size","land_sf","lb_ratio","year_built","nbhd","market_value",
    "per_unit","adj_size_pct","adj_age_pct","adj_quality_pct","adj_land_ratio_pct","adj_location_pct","adj_custom_pct","adj_sum_pct",
    "adj_per_unit","adj_total_value","notes"
  ];

  /* ------------- render ------------- */
  return (
    <div className="space-y-4">
      {/* Import + actions */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm cursor-pointer hover:border-slate-400">
          <input
            type="file"
            accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleFile}
          />
          Import CSV / Excel
        </label>
        <button
          onClick={addEmptyComp}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:border-slate-400"
        >
          + Add Comp
        </button>
        <button
          onClick={() => setCompMapOpen(true)}
          className={`rounded-md border px-3 py-1.5 text-sm ${
            compMapData
              ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-400"
              : "border-slate-300 bg-white hover:border-slate-400"
          }`}
          title={compMapData ? "Edit saved comp map" : "Generate a map from comp addresses"}
        >
          {compMapData ? "\u2713 Edit Map" : "Generate Map"}
        </button>

        {(() => {
		  const list =
			Array.isArray(supportedCads) && supportedCads.length
			  ? supportedCads
			  : [];

		  const tooltip =
			`Refresh Values pulls “Total Market” using the Packet Year.\n` +
			`Packet Year: ${coverCtx.taxYear}\n\n` +
			`Supported CADs:\n${list.join(", ") || "None yet"}`;

		  return (
			<>
			  <div className="inline-flex items-center gap-1">

				<button
				  onClick={doRefreshFromCAD}
				  disabled={isRefreshing}
				  className={`rounded-md px-3 py-1.5 text-sm border ${
					isRefreshing
					  ? "border-slate-200 bg-slate-100 text-slate-400"
					  : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-400"
				  }`}
				>
				  {isRefreshing ? "Refreshing…" : "Refresh Values"}
				</button>

				<button
				  type="button"
				  onClick={() => setShowCadInfo(true)}
				  className="h-7 w-7 rounded-full border border-emerald-400 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 flex items-center justify-center text-sm"
				  title={tooltip}
				  aria-label="Refresh Values info"
				>
				  ℹ
				</button>

			  </div>

			  <RefreshValuesModal
				open={showCadInfo}
				onClose={() => setShowCadInfo(false)}
				supportedCads={supportedCads}
				intro={
				  <div className="space-y-3 text-sm text-slate-700">
					<p>
					  The <strong>Refresh Values</strong> feature pulls the{" "}
					  <strong>Total Market Value</strong> directly from the
					  corresponding CAD website.
					</p>
					<p>
					  Values are fetched using the <strong>Packet Year</strong>.
					  <br />
					  Current Packet Year: <strong>{coverCtx.taxYear}</strong>
					</p>
					{/harris|hcad/i.test(coverCtx?.coverMeta?.property_info?.assessor || "") && (
					  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
						<div className="text-sm font-semibold text-amber-800 mb-1">
						  Harris County Note
						</div>
						<p className="text-sm text-amber-700">
						  Harris County values are pulled from the{" "}
						  <strong>initial appraisal roll</strong>, not live HCAD
						  data. These are the values as of the notice date and may
						  differ from the current HCAD website if values have been
						  adjusted.
						</p>
					  </div>
					)}
				  </div>
				}
			  />
			</>
		  );
		})()}

        {/* column picker */}
        <div className="relative ml-auto">
          <button
            onClick={() => setShowPicker((v) => !v)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:border-slate-400"
          >
            Columns
          </button>
          {showPicker && (
            <div className="absolute right-0 z-10 mt-1 w-80 rounded-lg border border-slate-200 bg-white p-3 shadow">
              <div className="text-xs text-slate-500 mb-2">Show in grid</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                {ORDER.map((k) => (
                  <label key={k} className="flex items-center gap-2">
                    <Checkbox
                      checked={!!C[k]}
                      onChange={(v) => setConfig((prev) => ({ ...prev, columns: { ...prev.columns, [k]: v } }))}
                    />
                    <span>
                      {k === "per_unit" ? perUnitLabel :
                       k === "adj_per_unit" ? `Adj ${perUnitLabel}` :
                       k === "adj_total_value" ? "Adj Total Value" :
                       k === "nbhd" ? "IMA / Neighborhood" :
                       k === "size" ? `Size (${unitNoun === "SF" ? "SF" : unitNoun})` :
                       k === "land_sf" ? "Land SF" :
                       k === "lb_ratio" ? "L:B Ratio" :
                       k === "year_built" ? "Year Built" :
                       k === "market_value" ? "Total Value" :
                       k === "class" ? "Quality" :
                       k === "adj_size_pct" ? "Size Adj" :
                       k === "adj_age_pct" ? "Age Adj" :
                       k === "adj_quality_pct" ? "Quality Adj" :
                       k === "adj_land_ratio_pct" ? "L:B Ratio Adj" :
                       k === "adj_location_pct" ? "Location Adj" :
                       k === "adj_custom_pct" ? "Custom Adj" :
                       (FIELD_DEFS[INDEX_BY_KEY[k]]?.label || k)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mapping dialog */}
      <MappingDialog
        open={mapUI.open}
        headers={mapUI.headers}
        sampleRows={mapUI.sample}
        unitNoun={unitNoun}
        initialFieldToIndex={toFieldToIndex(mapUI.guessed)}
        onCancel={() => setMapUI({ open: false, headers: [], sample: [], guessed: {} })}
        onImport={confirmImport}
      />

      <RefreshResultsDialog
        result={refreshResult}
        onClose={() => setRefreshResult(null)}
        onUncheckFailed={(failedAccts) => {
          const failedSet = new Set(failedAccts.map((a) => a.toUpperCase()));
          setRows((prev) =>
            prev.map((r) => {
              const parts = String(r.account || "")
                .split(",")
                .map((a) => a.trim().toUpperCase())
                .filter(Boolean);
              if (parts.some((p) => failedSet.has(p))) {
                return { ...r, use: false };
              }
              return r;
            })
          );
          setRefreshResult(null);
        }}
      />

      {/* Summary (now shows net + deduction when present) */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm flex flex-wrap items-center gap-4">
        <div><span className="text-slate-500">Comps used:</span> <span className="font-medium">{stats.count}</span></div>
        <div><span className="text-slate-500">Median Adj {perUnitLabel}:</span> <span className="font-medium">{fmtMoney2(stats.median_adj_per_unit)}</span></div>
        {deductionAmt > 0 && (
          <div><span className="text-slate-500">Less deduction:</span>{" "}
            <span className="font-medium">
              {fmtMoney0(deductionAmt)} ({deductionType === "lease_up" ? "Lease-Up" : "Manual"})
            </span>
          </div>
        )}
        <div><span className="text-slate-500">Indicated Value (net):</span> <span className="font-semibold">{fmtMoney0(indicatedNet)}</span></div>
      </div>

      {/* Auto rules */}
      <EUAutoAdjustRules
        rules={rules}
        onChange={setRules}
        unitLabel={unitNoun === "SF" ? "SF" : unitNoun}
        qualities={qualityValues}
        onApply={() => applyAutoAdjustments()}
      />

      {/* Comp selection criteria */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm space-y-3">
        <div className="font-semibold text-slate-800">Comp Selection Criteria</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Nbhd / Area</label>
            <input
              className="w-full rounded border border-slate-300 px-2 py-1"
              value={compCriteria.nbhd_area}
              onChange={(e) => updateCriteria({ nbhd_area: e.target.value })}
              placeholder="e.g., 5944.01"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Radius (miles)</label>
            <div className="flex items-center gap-1">
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.radius}
                onChange={(e) => updateCriteria({ radius: e.target.value.replace(/[^\d.]/g, "").replace(/^(\d*\.?\d{0,2}).*$/, "$1") })}
                placeholder="e.g., 5"
                inputMode="decimal"
              />
              <span className="text-sm text-slate-500 whitespace-nowrap">miles</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Type</label>
            <input
              className="w-full rounded border border-slate-300 px-2 py-1"
              value={compCriteria.type}
              onChange={(e) => updateCriteria({ type: e.target.value })}
              placeholder="e.g., Multifamily"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Age (Year Built range)</label>
            <div className="flex items-center gap-1">
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.age_min}
                onChange={(e) => updateCriteria({ age_min: e.target.value.replace(/[^\d]/g, "") })}
                placeholder="From"
                inputMode="numeric"
              />
              <span className="text-slate-400">–</span>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.age_max}
                onChange={(e) => updateCriteria({ age_max: e.target.value.replace(/[^\d]/g, "") })}
                placeholder="To"
                inputMode="numeric"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Size ({unitNoun} range)</label>
            <div className="flex items-center gap-1">
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.size_min}
                onChange={(e) => updateCriteria({ size_min: e.target.value.replace(/[^\d.]/g, "") })}
                placeholder="Min"
                inputMode="decimal"
              />
              <span className="text-slate-400">–</span>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.size_max}
                onChange={(e) => updateCriteria({ size_max: e.target.value.replace(/[^\d.]/g, "") })}
                placeholder="Max"
                inputMode="decimal"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Class</label>
            <input
              className="w-full rounded border border-slate-300 px-2 py-1"
              value={compCriteria.class}
              onChange={(e) => updateCriteria({ class: e.target.value })}
              placeholder="e.g., B, C"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Other</label>
          <input
            className="w-full rounded border border-slate-300 px-2 py-1"
            value={compCriteria.other || ""}
            onChange={(e) => updateCriteria({ other: e.target.value })}
            placeholder="Additional criteria..."
          />
        </div>
        <label className="flex items-center gap-2 pt-1">
          <Checkbox
            checked={!!compCriteria.none_omitted}
            onChange={(v) => updateCriteria({ none_omitted: v })}
          />
          <span className="text-sm text-slate-700">None omitted — all comps meeting these criteria were included</span>
        </label>
      </div>

      {/* NEW: Deductions card */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm space-y-3">
        <div className="font-semibold text-slate-800">Deductions (optional)</div>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Deduction Type</label>
            <select
              className="w-full rounded border border-slate-300 px-2 py-1"
              value={deductionType}
              onChange={(e) => setDeductionType(e.target.value)}
            >
              <option value="none">None</option>
              <option value="lease_up">Lease-Up (NPV)</option>
              <option value="manual">Manual ($)</option>
            </select>
          </div>

          {deductionType === "manual" && (
            <div>
              <label className="block text-xs text-slate-500 mb-1">Manual Deduction ($)</label>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={manualDeduction}
                onChange={(e) => setManualDeduction(e.target.value.replace(/[^\d.-]/g, ""))}
                inputMode="decimal"
                placeholder="e.g., 250000"
              />
            </div>
          )}
        </div>

        {deductionType === "lease_up" && (
          <>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Annual PGI (stabilized)</label>
                <input
                  className="w-full rounded border border-slate-300 px-2 py-1"
                  value={luPGI}
                  onChange={(e)=> setLuPGI(e.target.value.replace(/[^\d.-]/g, ""))}
                  inputMode="decimal"
                  placeholder="e.g., 1,200,000"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Stabilized Vacancy (%)</label>
                <input
                  className="w-full rounded border border-slate-300 px-2 py-1"
                  value={luVac}
                  onChange={(e)=> setLuVac(e.target.value.replace(/[^\d.%-]/g, ""))}
                  inputMode="decimal"
                  placeholder="e.g., 10%"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Annual OpEx</label>
                <input
                  className="w-full rounded border border-slate-300 px-2 py-1"
                  value={luOpex}
                  onChange={(e)=> setLuOpex(e.target.value.replace(/[^\d.-]/g, ""))}
                  inputMode="decimal"
                  placeholder="e.g., 350,000"
                />
              </div>
            </div>

            <LeaseUpCalculator
              value={leaseUp || undefined}
              onChange={setLeaseUp}
              pgiAnnual={num(luPGI || 0)}
              opexAnnual={num(luOpex || 0)}
              stabilizedVacancyPct={luVacNum}
              defaultDiscountAnnualPct={0}
              totalBuildingSF={coverCtx.unitType === "SF" ? coverCtx.sizeFromCover : 0}
            />
          </>
        )}
      </div>

      {/* Table */}
      <div className="overflow-auto rounded-xl border border-slate-200">
        <table className="min-w-[1900px] w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left sticky left-0 bg-slate-50 z-10 w-[140px]">Role</th>
              {C.use && <th className="px-2 py-2 text-left sticky left-[140px] bg-slate-50 z-10">
                <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Check / uncheck all comps">
                  <Checkbox
                    checked={comps.length > 0 && comps.every((r) => !!r.use)}
                    onChange={(v) => setRows((prev) => prev.map((r) => r.id === config.subjectId ? r : { ...r, use: v }))}
                  />
                  <span>Use</span>
                </label>
              </th>}
              {C.account        && <th className="px-2 py-2 text-left">Account #</th>}
              {C.address        && <th className="px-2 py-2 text-left">Address</th>}
              {C.property_type  && <th className="px-2 py-2 text-left">Property Type</th>}
              {C.class          && <th className="px-2 py-2 text-left">Quality</th>}
              {C.size           && <th className="px-2 py-2 text-right">Size ({unitNoun})</th>}
              {C.land_sf        && <th className="px-2 py-2 text-right">Land SF</th>}
              {C.lb_ratio       && <th className="px-2 py-2 text-right">L:B Ratio</th>}
              {C.year_built     && <th className="px-2 py-2 text-right">Year Built</th>}
              {C.nbhd           && <th className="px-2 py-2 text-left">IMA / Neighborhood</th>}
              {C.market_value   && <th className="px-2 py-2 text-right">Total Value</th>}
              {C.per_unit       && <th className="px-2 py-2 text-right">{perUnitLabel}</th>}
              {C.adj_size_pct   && <th className="px-2 py-2 text-right">Size Adj%</th>}
              {C.adj_age_pct    && <th className="px-2 py-2 text-right">Age Adj%</th>}
              {C.adj_quality_pct&&<th className="px-2 py-2 text-right">Quality Adj%</th>}
              {C.adj_land_ratio_pct&&<th className="px-2 py-2 text-right">L:B Ratio Adj%</th>}
              {C.adj_location_pct&&<th className="px-2 py-2 text-right">Location Adj%</th>}
              {C.adj_custom_pct && <th className="px-2 py-2 text-right">Custom Adj%</th>}
              {C.adj_sum_pct    && <th className="px-2 py-2 text-right">Adj Sum%</th>}
              {C.adj_per_unit   && <th className="px-2 py-2 text-right">Adj {perUnitLabel}</th>}
              {C.adj_total_value&& <th className="px-2 py-2 text-right">Adj Total Value</th>}
              {C.notes          && <th className="px-2 py-2 text-left">Notes</th>}
              <th className="px-2 py-2"></th>
            </tr>
          </thead>

          <tbody>
            {withCalcs.map((r) => {
              const isSubject = r.id === config.subjectId;
              const lockSize = isSubject || !!rules.size?.enabled;
              const lockAge  = isSubject || !!rules.age?.enabled;
              const lockQual = isSubject || !!rules.quality?.enabled;
              const lockLandRatio = isSubject || !!rules.landRatio?.enabled;
              return (
                <tr key={r.id} className="border-t">
                  {/* role */}
                  <td className="px-2 py-1 sticky left-0 bg-white z-10 w-[140px]">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="subject"
                        checked={isSubject}
                        onChange={() => setConfig((p) => ({ ...p, subjectId: r.id }))}
                        title="Set as Subject (Subject is never adjusted)"
                      />
                      <span className={isSubject ? "font-medium" : ""}>{isSubject ? "Subject" : "Comp"}</span>
                    </label>
                  </td>

                  {C.use && (
                    <td className="px-2 py-1 sticky left-[140px] bg-white z-10">
                      <Checkbox checked={!!r.use} onChange={(v) => setRow(r.id, { use: v })} disabled={isSubject} />
                    </td>
                  )}

                  {C.account && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.account || ""} onChange={(e) => setRow(r.id, { account: e.target.value })} />
                    </td>
                  )}

                  {C.address && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.address || ""} onChange={(e) => setRow(r.id, { address: e.target.value })} />
                    </td>
                  )}

                  {C.property_type && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.property_type || ""} onChange={(e) => setRow(r.id, { property_type: e.target.value })} />
                    </td>
                  )}

                  {C.class && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.class || ""} onChange={(e) => setRow(r.id, { class: e.target.value })} placeholder="e.g., A, B, C, Renovated, etc." />
                    </td>
                  )}

                  {C.size && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.size} onChange={(v) => setRow(r.id, { size: v.replace(/[^\d.]/g, "") })} inputMode="decimal" />
                    </td>
                  )}

                  {C.land_sf && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.land_sf} onChange={(v) => setRow(r.id, { land_sf: v.replace(/[^\d.]/g, "") })} inputMode="decimal" />
                    </td>
                  )}

                  {C.lb_ratio && (
                    <td className="px-2 py-1 text-right font-medium">
                      {Number.isFinite(r._lb_ratio) && r._lb_ratio > 0 ? r._lb_ratio.toFixed(2) : "—"}
                    </td>
                  )}

                  {C.year_built && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.year_built} onChange={(v) => setRow(r.id, { year_built: v.replace(/[^\d]/g, "") })} inputMode="numeric" />
                    </td>
                  )}

                  {C.nbhd && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.nbhd || ""} onChange={(e) => setRow(r.id, { nbhd: e.target.value })} />
                    </td>
                  )}

                  {C.market_value && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.market_value} onChange={(v) => setRow(r.id, { market_value: v.replace(/[^\d.-]/g, "") })} inputMode="decimal" />
                    </td>
                  )}

                  {C.per_unit && <td className="px-2 py-1 text-right">{fmtMoney2(r._per_unit)}</td>}

                  {C.adj_size_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_size_pct} onChange={(v) => !lockSize && setRow(r.id, { adj_size_pct: sanitizePctInt(v) })} suffix="%" disabled={lockSize} inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_age_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_age_pct} onChange={(v) => !lockAge && setRow(r.id, { adj_age_pct: sanitizePctInt(v) })} suffix="%" disabled={lockAge} inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_quality_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_quality_pct} onChange={(v) => !lockQual && setRow(r.id, { adj_quality_pct: sanitizePctInt(v) })} suffix="%" disabled={lockQual} inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_land_ratio_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_land_ratio_pct} onChange={(v) => !lockLandRatio && setRow(r.id, { adj_land_ratio_pct: sanitizePctInt(v) })} suffix="%" disabled={lockLandRatio} inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_location_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_location_pct} onChange={(v) => setRow(r.id, { adj_location_pct: sanitizePctInt(v) })} suffix="%" inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_custom_pct && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell value={r.adj_custom_pct} onChange={(v) => setRow(r.id, { adj_custom_pct: sanitizePctInt(v) })} suffix="%" inputMode="decimal" />
                    </td>
                  )}
                  {C.adj_sum_pct && (
                    <td className="px-2 py-1 text-right font-medium">
                      {Number.isFinite(r._adj_sum_pct) ? `${(r._adj_sum_pct * 100).toFixed(2)}%` : "—"}
                    </td>
                  )}
                  {C.adj_per_unit    && <td className="px-2 py-1 text-right font-medium">{fmtMoney2(r._adj_per_unit)}</td>}
                  {C.adj_total_value && <td className="px-2 py-1 text-right font-medium">{fmtMoney0(r._adj_total_value)}</td>}

                  {C.notes && (
                    <td className="px-2 py-1">
                      <input className="w-full rounded border border-slate-300 px-2 py-1" value={r.notes || ""} onChange={(e) => setRow(r.id, { notes: e.target.value })} />
                    </td>
                  )}

                  <td className="px-2 py-1">
                    {!isSubject && (
                      <button
                        onClick={() => removeRow(r.id)}
                        className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-rose-700 hover:border-rose-400"
                        title="Remove comp"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Actions footer */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={addEmptyComp}
          className="rounded-md px-3 py-2 border border-slate-200 bg-white hover:border-slate-300"
        >
          + Add Comp
        </button>

        <div className="ml-auto text-xs text-slate-600">
          Percent entries are whole numbers (e.g., <b>5</b> = 5%). Subject is never adjusted.
        </div>
      </div>

      {/* Comp Map Modal */}
      <CompMapModal
        open={compMapOpen}
        onClose={() => setCompMapOpen(false)}
        onSave={(md) => {
          setCompMapData(md);
          setCompMapOpen(false);
          // Build the full section payload with the new map_data and save immediately
          const payload = {
            equal_uniform: {
              rows,
              config: { ...config, auto_rules: rules, subjectId: config.subjectId },
              indicated_value: indicatedNet,
              indicated_value_before_deductions: stats.indicated_value,
              deduction_type: deductionType,
              manual_deduction: num(manualDeduction || 0),
              lease_up: leaseUp || null,
              pgi_total: num(luPGI || 0),
              vacancy: `${luVacNum}%`,
              map_data: md,
              comp_criteria: { ...compCriteria, unit_noun: unitNoun },
              summary: {
                comps_used: stats.count,
                median_adj_per_unit: stats.median_adj_per_unit,
              },
            },
          };
          onSave?.(payload);
        }}
        rows={rows}
        subjectId={config.subjectId}
        assessor={coverCtx?.coverMeta?.property_info?.assessor || ""}
        state={coverCtx?.coverMeta?.property_info?.state || ""}
        existingMapData={compMapData}
      />
    </div>
  );
}
